import {ISingleVoyage} from "@containers/singleVoyage/types/voyage";
import {
	ISyncChangeVoyageTemplate,
	IVoyageTemplateSingleVoyage
} from "@containers/singleVoyage/voyageTemplates/types";

export interface IStopDiffRow {
	/** position in the template, from 0 */
	ordinal: number
	name: string
	change: ISyncChangeVoyageTemplate | null
	/** position the stop had in the voyage, when it differs */
	wasOrdinal: number | null
}

export interface IRemovedStopDiff {
	name: string
	/** position the stop had in the voyage, from 0 */
	wasOrdinal: number | null
}

export interface IStopsDiff {
	rows: IStopDiffRow[]
	removed: IRemovedStopDiff[]
}

/**
 * Lays the template's stops out in order and marks what sync does to each. Stops are paired with
 * the voyage's the way the backend pairs them - by stop template and occurrence - so a stop listed
 * twice is tracked twice. The backend's changes decide added/moved/changed; the voyage only tells
 * where a kept stop used to be and which stops go away.
 */
export const buildStopsDiff = ({template, voyage, changes}: {
	template: IVoyageTemplateSingleVoyage | null
	voyage: ISingleVoyage | null
	changes: ISyncChangeVoyageTemplate[]
}): IStopsDiff => {
	const templateStops = (template?.waypoints ?? []).filter((waypoint) => waypoint.waypointTemplate)

	const voyageStops = (voyage?.waypoints ?? [])
		.filter((waypoint) => waypoint.type !== 'SHAPING_POINT' && waypoint.status !== 'DELETED')
		.slice()
		.sort((a, b) => a.shapeOrdinal - b.shapeOrdinal)

	const oldOrdinalByKey: Record<string, number> = {}
	const voyageOccurrences: Record<string, number> = {}
	voyageStops.forEach((waypoint, index) => {
		const id = waypoint.waypointTemplate?.id
		if (!id) {
			return
		}
		voyageOccurrences[id] = (voyageOccurrences[id] ?? 0) + 1
		oldOrdinalByKey[`${id}|${voyageOccurrences[id]}`] = index
	})

	const changeByOrdinal: Record<number, ISyncChangeVoyageTemplate> = {}
	changes
		.filter((change) => change.ordinal !== null && ['ADD', 'MOVE', 'UPDATE'].includes(change.type))
		.forEach((change) => changeByOrdinal[change.ordinal] = change)

	const templateOccurrences: Record<string, number> = {}
	const rows = templateStops.map((waypoint, ordinal): IStopDiffRow => {
		const id = waypoint.waypointTemplate.id
		templateOccurrences[id] = (templateOccurrences[id] ?? 0) + 1
		const oldOrdinal = oldOrdinalByKey[`${id}|${templateOccurrences[id]}`]
		const change = changeByOrdinal[ordinal] ?? null
		return {
			ordinal,
			name: waypoint.waypointTemplate.name,
			change,
			wasOrdinal: change?.type !== 'ADD' && oldOrdinal !== undefined && oldOrdinal !== ordinal ? oldOrdinal : null,
		}
	})

	const removed = changes
		.filter((change) => change.type === 'REMOVE')
		.map((change): IRemovedStopDiff => ({name: change.stopName ?? '', wasOrdinal: change.ordinal}))

	return {rows, removed}
}
